import React, { useState, useEffect } from 'react';
import styles from './ProductDetailModal.module.css';
import {
    ProductAnalysisData,
    ProductCustomer,
    fetchProductCustomers,
    ProductExtraDetails,
    fetchProductDetails,
} from '../../services/productsAPI';

import CloseIcon from '@mui/icons-material/Close';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

interface ProductDetailModalProps {
    product: ProductAnalysisData;
    onClose: () => void;
    startDate: string;
    endDate: string;
}

const formatCurrency = (value: any) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 'R$ 0,00';
    return num.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    });
};

const formatNumber = (value: any) => {
    const num = parseInt(value, 10);
    if (isNaN(num)) return '0';
    return num.toLocaleString('pt-BR');
};

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({ product, onClose, startDate, endDate }) => {
    const [ranking, setRanking] = useState<ProductCustomer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [extraDetails, setExtraDetails] = useState<ProductExtraDetails | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(true);

    useEffect(() => {
        const loadCustomerData = async () => {
            if (!product || !startDate || !endDate) return;
            try {
                setLoading(true);
                const response = await fetchProductCustomers(product.Produto, { dataI: startDate, dataF: endDate });
                setRanking(response.data.customers || []);
                setError(null);
            } catch (err) {
                setError('Não foi possível carregar o ranking de clientes.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadCustomerData();
    }, [product, startDate, endDate]);

    useEffect(() => {
        const loadExtraDetails = async () => {
            if (!product) return;
            try {
                setDetailsLoading(true);
                const response = await fetchProductDetails(product.Produto);
                setExtraDetails(response.data);
            } catch (err) {
                console.error('Erro ao buscar detalhes extras:', err);
                setExtraDetails(null);
            } finally {
                setDetailsLoading(false);
            }
        };
        loadExtraDetails();
    }, [product]);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    if (!product) return null;

    const maxRankingRevenue = ranking.length > 0 ? ranking[0].FaturamentoTotal : 0;
    const hasImage = extraDetails?.img;
    const productName = extraDetails?.DESCRICAO || product.Produto;

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <h2 className={styles.title}>{productName}</h2>
                        <span className={styles.badge}>{product.Linha}</span>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    {/* Top Section: Image + Info side by side */}
                    <div className={styles.topSection}>
                        <div className={styles.imageBlock}>
                            {hasImage ? (
                                <img src={extraDetails.img!} alt={product.Produto} className={styles.image} />
                            ) : (
                                <div className={styles.noImage}>
                                    <ImageOutlinedIcon sx={{ fontSize: 36, color: '#cbd5e1' }} />
                                </div>
                            )}
                        </div>

                        <table className={styles.infoTable}>
                        <tbody>
                            <tr>
                                <td className={styles.infoCell}>
                                    <span className={styles.infoLabel}>Referência</span>
                                    <span className={styles.infoValue}>{product.Produto}</span>
                                </td>
                                <td className={styles.infoCell}>
                                    <span className={styles.infoLabel}>CA</span>
                                    <span className={styles.infoValue}>{detailsLoading ? '...' : extraDetails?.CA || 'N/A'}</span>
                                </td>
                                <td className={styles.infoCell}>
                                    <span className={styles.infoLabel}>Preço Médio</span>
                                    <span className={styles.infoValue}>{formatCurrency(product.PrecoMedio)}</span>
                                </td>
                                <td className={styles.infoCell}>
                                    <span className={styles.infoLabel}>Margem</span>
                                    <span className={styles.infoValue} style={{ color: (product.MargemPercentual || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                                        {(product.MargemPercentual || 0).toFixed(1)}%
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.infoCell}>
                                    <span className={styles.infoLabel}>Faturamento</span>
                                    <span className={styles.infoValue}>{formatCurrency(product.FaturamentoTotal)}</span>
                                </td>
                                <td className={styles.infoCell}>
                                    <span className={styles.infoLabel}>Pares Vendidos</span>
                                    <span className={styles.infoValue}>{formatNumber(product.QuantidadeTotal)}</span>
                                </td>
                                <td className={styles.infoCell}>
                                    <span className={styles.infoLabel}>Clientes</span>
                                    <span className={styles.infoValue}>{formatNumber(product.NumClientes)}</span>
                                </td>
                                <td className={styles.infoCell}></td>
                            </tr>
                        </tbody>
                    </table>
                    </div>

                    {/* Ranking */}
                    <div className={styles.rankingSection}>
                        <div className={styles.rankingHead}>
                            <PeopleOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                            <span className={styles.rankingTitle}>Principais Clientes</span>
                            <span className={styles.rankingCount}>{ranking.length} cliente{ranking.length !== 1 ? 's' : ''}</span>
                        </div>

                        {loading ? (
                            <div className={styles.stateBox}>Carregando...</div>
                        ) : error ? (
                            <div className={styles.stateBox} style={{ color: '#dc2626' }}>{error}</div>
                        ) : ranking.length > 0 ? (
                            <div className={styles.rankingList}>
                                {ranking.map((customer, index) => (
                                    <div key={index} className={styles.rankRow}>
                                        <div className={styles.rankInfo}>
                                            <span className={`${styles.rankPos} ${index < 3 ? styles.rankPosTop : ''}`}>
                                                {String(index + 1).padStart(2, '0')}
                                            </span>
                                            <span className={styles.rankName}>{customer.NomeCliente}</span>
                                            <span className={styles.rankValue}>{formatCurrency(customer.FaturamentoTotal)}</span>
                                        </div>
                                        <div className={styles.rankBar}>
                                            <div className={styles.rankFill} style={{ width: `${(customer.FaturamentoTotal / maxRankingRevenue) * 100}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.emptyState}>
                                <TrendingUpOutlinedIcon sx={{ fontSize: 32, color: '#e2e8f0' }} />
                                <p>Nenhum cliente comprou este produto no período.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailModal;
